import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import { context } from '@adornis/baseql/server/context.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { useObservable } from '@adornis/functional-lit/stateful.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { type DateArg, type FormatOptions, format } from 'date-fns';
import type { Filter, FindOptions, UpdateFilter } from 'mongodb';
import { map } from 'rxjs';
import type {
  ExpandEntityRecursively,
  Full,
  MongoSelect,
  mEntityData,
  PartialMongoSelection,
  RemoveIndexSignature,
} from './selectionset-types.js';
import { mongo } from './server/mongo.js';

export function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function isDefined<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

// TODO: use constructValue instead of this
export function mongoDataToEntity(data: Record<string, any>) {
  const clazz = data._class;
  if (!clazz) {
    logger.error({ data }, 'no class found');
    throw new Error('No class found');
  }

  const node = baseqlMetaData.entities.find(entity => entity._class === clazz);
  if (!node) throw new Error('Entity node not found');
  const entity = node.type() as Maybe<typeof AdornisEntity>;
  if (!entity) throw new Error('Entity type not found');
  return new (entity as any)(data);
}

export function getChildEntities(parent: typeof AdornisEntity) {
  const entities: (typeof AdornisEntity)[] = [];
  baseqlMetaData.entities.forEach(node => {
    if (node._class === parent._class) return; // don't include the parent itself
    let _parent = node.parent;
    while (_parent) {
      if (_parent._class === parent._class) {
        entities.push(node.type() as typeof AdornisEntity);
        return true;
      }
      _parent = _parent.parent;
    }
  });

  return entities;
}

// TODO: fix this type
export function gqlEnumValues<T>(_enum: any): T[] {
  return Object.keys(_enum as any).filter(key => typeof key === 'string' && !key.startsWith('_')) as any;
}

// date
export function formatIfDefined(
  date: (DateArg<Date> & {}) | null | undefined,
  formatStr: string,
  options?: FormatOptions,
) {
  if (date === null || date === undefined) return null;
  return format(date, formatStr, options);
}

// hooks

export function useTranslation() {
  return useObservable(() => globalLanguageSubject.pipe(map(() => clientTranslator)), []);
}

export function useTheme() {
  return useObservable(() => DesignSystem.currentTheme, []);
}

// get user server (cache it)
const cachedUserSelectionSet = {
  _id: 1,
  isAdmin: 1,
  isLecturer: 1,
  isSuperAdmin: 1,
  adminRole: { contexts: 1 },
  lecturerRole: { contexts: 1 },
  roles: { _id: 1, name: 1, permissions: 1, contexts: 1 },
  email: 1,
  title: 1,
  username: 1,
  firstName: 1,
  lastName: 1,
  whatsappNotification: 1,
  suspentions: { _id: 1, institutionID: 1 },
  whatsappPhone: 1,
  dateOfBirth: 1,
  receivesNewsletter: 1,
} as const;

// TODO: put that in buchungsheld code to get correct types
export type ServerCachedUser = any;
// export type ServerCachedUser = NarrowedEntity<AdornisUser, typeof cachedUserSelectionSet>;

let testingUser: ServerCachedUser | null = null;
export function setTestingUser(user: ServerCachedUser | null) {
  if (baseConfig.get('DEPLOYMENT') !== 'testing') throw new Error('This operation is only allowed in testing');
  testingUser = user;
}

export async function readThroughUserCache<U extends { _id?: string | null }>(
  ctx: { cachedUser?: U | null; cachedUserTime?: number },
  userID: string | undefined,
  fetchUser: () => Promise<U | null>,
  now: number,
  maxAgeMs = 2000,
): Promise<U | null> {
  if (
    ctx.cachedUser &&
    ctx.cachedUser._id === userID &&
    ctx.cachedUserTime !== undefined &&
    now - ctx.cachedUserTime < maxAgeMs
  ) {
    return ctx.cachedUser;
  }

  const user = await fetchUser();
  if (!user) return null;

  ctx.cachedUser = user;
  ctx.cachedUserTime = now;
  return user;
}

export const getCurrentUserServer = async (): Promise<ServerCachedUser | null> => {
  if (baseConfig.get('DEPLOYMENT') === 'testing') return testingUser;
  if (context.serverContext) return null;

  return readThroughUserCache<ServerCachedUser>(
    context as typeof context & { cachedUser?: ServerCachedUser | null; cachedUserTime?: number },
    context.userID,
    // @ts-ignore TODO: fix types
    () => getMyself<AdornisUser>()(cachedUserSelectionSet),
    Date.now(),
  );
};

export async function getCurrentUserServerOrFail() {
  const user = await getCurrentUserServer();
  if (!user) throw new ExpectedError('not authenticated');
  return user;
}

// load Raw mongo data
export async function loadRawMongoData<T extends typeof MongoEntity>(
  EntityClass: T,
  id: string,
): Promise<Maybe<EntityData<T['prototype']>>>;
export async function loadRawMongoData<T extends typeof MongoEntity>(
  EntityClass: T,
  ids: string[],
): Promise<Array<EntityData<T['prototype']>>>;
export async function loadRawMongoData<T extends typeof MongoEntity>(
  EntityClass: T,
  filter: Filter<EntityData<T['prototype']>>,
  options?: FindOptions,
): Promise<Array<EntityData<T['prototype']>>>;
export async function loadRawMongoData<T extends typeof MongoEntity>(
  EntityClass: T,
  filter: string | string[] | Filter<EntityData<T['prototype']>>,
  options?: FindOptions,
): Promise<Maybe<EntityData<T['prototype']>> | Array<EntityData<T['prototype']>>>;
export async function loadRawMongoData<T extends typeof MongoEntity>(
  EntityClass: T,
  filter: string | string[] | Filter<EntityData<T['prototype']>>,
  options?: FindOptions,
): Promise<Maybe<EntityData<T['prototype']>> | Array<EntityData<T['prototype']>>> {
  const collection = mongo().collection<EntityData<T['prototype']>>(EntityClass._collectionName);

  if (Array.isArray(filter)) {
    // @ts-expect-error TS can't verify { _id: { $in: string[] } } against Filter<EntityData<T>> for generic T
    return collection.find({ _id: { $in: filter } }, options).toArray();
  }

  if (typeof filter === 'string') {
    // @ts-expect-error TS can't verify { _id: string } against Filter<EntityData<T>> for generic T
    return collection.findOne({ _id: filter }, options);
  }

  // @ts-expect-error WithId<EntityData<T>> is not assignable to EntityData<T> for generic T
  return collection.find(filter, options).toArray();
}

// authorizatioin

export async function validateAuthorization(validateAuthorization: (user: ServerCachedUser) => boolean) {
  const user = await getCurrentUserServerOrFail();
  if (!validateAuthorization(user)) {
    throw new ExpectedError('Permission not granted!');
  }
}

// entity lookup

export async function loadRawEntityDataByID<
  T extends typeof MongoEntity,
  Tsel extends PartialMongoSelection<mEntityData<T['prototype']>> = Full<
    PartialMongoSelection<mEntityData<T['prototype']>>
  >,
>(
  EntityClass: T,
  id: string,
  gqlFields?: Tsel,
): Promise<ExpandEntityRecursively<MongoSelect<mEntityData<T['prototype']>, Tsel>> | null> {
  const collection = mongo().collection<mEntityData<MongoEntity>>(EntityClass._collectionName);
  const aggregation = [{ $match: { _id: id } }] as any[];
  if (gqlFields) aggregation.push({ $project: gqlFields });
  const result = await collection.aggregate(aggregation).toArray();

  return (result[0] as any) ?? null;
}

export async function loadRawEntityDataByIDOrFail<
  T extends typeof MongoEntity,
  Tsel extends PartialMongoSelection<mEntityData<T['prototype']>> = Full<
    PartialMongoSelection<mEntityData<T['prototype']>>
  >,
>(
  EntityClass: T,
  id: string,
  gqlFields?: Tsel,
): Promise<ExpandEntityRecursively<MongoSelect<mEntityData<T['prototype']>, Tsel>>> {
  const result = await loadRawEntityDataByID(EntityClass, id, gqlFields);
  if (!result) throw new Error(`No ${EntityClass._class} found with id ${id}`);
  return result as any;
}

export async function loadRawEntityDataByIDs<
  T extends typeof MongoEntity,
  Tsel extends PartialMongoSelection<mEntityData<T['prototype']>> = Full<
    PartialMongoSelection<mEntityData<T['prototype']>>
  >,
>(
  EntityClass: T,
  ids: string[],
  gqlFields?: Tsel,
): Promise<RemoveIndexSignature<ExpandEntityRecursively<MongoSelect<mEntityData<T['prototype']>, Tsel>>>[]> {
  const collection = mongo().collection<EntityData<MongoEntity>>(EntityClass._collectionName);
  const aggregation = [{ $match: { _id: { $in: ids } } }] as any[];
  if (gqlFields) aggregation.push({ $project: gqlFields });
  const result = await collection.aggregate(aggregation).toArray();
  return result as any;
}

export async function loadRawEntityDataByQuery<
  T extends typeof MongoEntity,
  Tsel extends PartialMongoSelection<mEntityData<T['prototype']>> = Full<
    PartialMongoSelection<mEntityData<T['prototype']>>
  >,
>(
  EntityClass: T,
  filter: Record<string, any>,
  gqlFields?: Tsel,
): Promise<ExpandEntityRecursively<MongoSelect<mEntityData<T['prototype']>, Tsel>>[]> {
  const collection = mongo().collection<mEntityData<MongoEntity>>(EntityClass._collectionName);
  const aggregation = [{ $match: filter }] as any[];
  if (gqlFields) aggregation.push({ $project: gqlFields });
  const result = await collection.aggregate(aggregation).toArray();
  return result as any;
}

export function extractNonResolvedEntityData(
  target: object,
  entity: typeof MongoEntity,
  data: mEntityData<MongoEntity>,
) {
  for (const field of Object.values(entity.fields) as BaseQLFieldDefinition[]) {
    if (!(field.name in data)) continue; // ignore unknown fields
    if (field.options.resolve && !field.options.includeInParameter) continue; // do not include resolved fields
    const value = data[field.name];

    // array
    if (field.arrayDepth() > 0) {
      if (!Array.isArray(value)) {
        logger.error({ field, data, value }, 'entityData array has to be an array');
        throw new Error('entityData array has to be an array');
      }

      target[field.name] = [];
      for (const arrayItemValue of value) {
        if (Array.isArray(arrayItemValue)) {
          throw new Error('this is currently not supported');
        }

        if (typeof arrayItemValue === 'object') {
          if (!('_class' in arrayItemValue)) {
            logger.error(
              { field, data, value, arrayValue: arrayItemValue },
              'entityData array item has to have _class',
            );
            throw new Error('entityData has to have _class');
          }
          const node = baseqlMetaData.entities.find(c => c._class === arrayItemValue._class);
          if (!node) throw new Error(`Could not find entity with name: ${arrayItemValue._class}`);
          const arrayItemEntity = node.type() as typeof MongoEntity;
          const arrayItemData = {};
          extractNonResolvedEntityData(arrayItemData, arrayItemEntity, arrayItemValue);
          target[field.name].push(arrayItemData);
          continue;
        }

        target[field.name].push(arrayItemValue);
      }
    }

    // date
    else if ((field.type() as any) instanceof Date && value !== null && value !== undefined) {
      target[field.name] = value;
    }

    // object
    else if ((field.type() as any) instanceof AdornisEntity && value !== null && value !== undefined) {
      if (!('_class' in value)) {
        logger.error({ field, data, value }, 'entityData has to have _class');
        throw new Error('entityData has to have _class');
      }
      const node = baseqlMetaData.entities.find(c => c._class === value._class);
      if (!node) throw new Error(`Could not find entity with name: ${value._class}`);
      const subEntity = node.type() as typeof MongoEntity;
      target[field.name] = {};
      extractNonResolvedEntityData(target[field.name], subEntity, value);
    }

    // literal
    else {
      target[field.name] = value;
    }
  }
}

// test extractNonResolvedEntityData
export async function testExtractNonResolvedEntityData() {
  // TODO: use independent entities for testing
  //   await delay(100);
  //   const Participation = await import('@adornis/buchungsheld-db/entities/Participation.js').then(m => m.Participation);
  //   const ParticipationWorkshop = await import('@adornis/buchungsheld-db/entities/ParticipationWorkshop.js').then(
  //     m => m.ParticipationWorkshop,
  //   );
  //   const Workshop = await import('@adornis/buchungsheld-db/entities/workshop.js').then(m => m.Workshop);
  //   // prettier-ignore
  //   const WorkshopAffiliation = await import('@adornis/buchungsheld-db/entities/workshop.js').then( m => m.WorkshopAffiliation);
  //   // prettier-ignore
  //   const TextMultipleLangItem = await import('@adornis/buchungsheld-db/entities/TextMultipleLangItem.js').then( m => m.TextMultipleLangItem);
  //   (() => {
  //     const data = {
  //       _id: 'foo',
  //       _class: Participation._class,
  //       userID: '123',
  //       stuffThatNotExists: 'baz',
  //       user: {
  //         _id: '123',
  //         _class: 'User',
  //         firstName: 'John Doe',
  //       },
  //     };
  //     const target = {} as any;
  //     extractNonResolvedEntityData(target, Participation, data);
  //     logger.info({ target }, 'target');
  //     if (target.userID !== '123') throw new Error('userID should be 123');
  //     if (target.user) throw new Error('user should be ommited');
  //     if (target.stuffThatNotExists) throw new Error('stuffThatNotExists should be ommited');
  //   })();
  //   (() => {
  //     const data = {
  //       _id: 'foo',
  //       _class: Workshop._class,
  //       translatedDescriptionHTML: [
  //         {
  //           _id: 'bar',
  //           _class: TextMultipleLangItem._class,
  //           langKey: 'en',
  //           text: 'foo',
  //         },
  //       ],
  //       affiliation: {
  //         _id: 'bar',
  //         _class: WorkshopAffiliation._class,
  //         affiliationID: 'foo',
  //       },
  //     };
  //     const target = {} as any;
  //     extractNonResolvedEntityData(target, Workshop, data);
  //     logger.info({ target }, 'target');
  //     if (!target.affiliation) throw new Error('affiliation should not be ommited');
  //     if (target.affiliation.affiliationID !== 'foo') throw new Error('affiliation should be "foo"');
  //     if (!target.translatedDescriptionHTML) throw new Error('translatedDescriptionHTML should not be included');
  //     if (target.translatedDescriptionHTML.length !== 1)
  //       throw new Error('translatedDescriptionHTML should have length 1');
  //     if (target.translatedDescriptionHTML[0].langKey !== 'en') throw new Error('langKey should be "en"');
  //     if (target.translatedDescriptionHTML[0].text !== 'foo') throw new Error('text should be "foo"');
  //   })();
  //   (() => {
  //     const data = {
  //       _id: 'abc',
  //       _class: ParticipationWorkshop._class,
  //       productID: '123',
  //       appointmentParticipations: [
  //         {
  //           _class: 'AppointmentParticipation',
  //           _id: '456',
  //           appointmentID: 'baz',
  //         },
  //       ],
  //     };
  //     const target = {} as any;
  //     extractNonResolvedEntityData(target, ParticipationWorkshop, data);
  //     logger.info({ target }, 'target');
  //     if (!target.appointmentParticipations) throw new Error('appointmentParticipations should not be ommited');
  //     if (target.appointmentParticipations.length !== 1)
  //       throw new Error('appointmentParticipations should have length 1');
  //     if (target.appointmentParticipations[0].appointmentID !== 'baz') throw new Error('appointmentID should be "baz"');
  //   })();
}

export async function saveEntityData<T extends typeof MongoEntity>(data: Partial<EntityData<T['prototype']>>) {
  if (!data._id) throw new Error('entityData has to have _id');
  if (!data._class) throw new Error('entityData has to have _class');

  const node = baseqlMetaData.entities.find(c => c._class === data._class);
  if (!node) throw new Error(`Could not find entity with name: ${data._class}`);
  const entity = node.type() as typeof MongoEntity;

  const collection = mongo().collection<EntityData<MongoEntity>>(entity._collectionName);

  const _entityData = (() => {
    if (data instanceof AdornisEntity) return data.toJSON();
    else {
      const target = {};
      extractNonResolvedEntityData(target, entity, data as any);
      return target;
    }
  })();

  await collection.updateOne({ _id: data._id }, { $set: _entityData }, { upsert: true });
}

export async function mongoUpdateOne(
  entity: typeof MongoEntity,
  filter: Filter<EntityData<MongoEntity>>,
  update: UpdateFilter<EntityData<MongoEntity>>,
) {
  const collection = mongo().collection<EntityData<MongoEntity>>(entity._collectionName);
  await collection.updateOne(filter, update);
}

export async function mongoUpdateMany(
  entity: typeof MongoEntity,
  filter: Filter<EntityData<MongoEntity>>,
  update: UpdateFilter<EntityData<MongoEntity>>,
) {
  const collection = mongo().collection<EntityData<MongoEntity>>(entity._collectionName);
  await collection.updateMany(filter, update);
}
